import React, { useEffect, useState } from 'react';
import { supabase, TABLES, SafeIcon } from '../../lib/core';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { 
  FiShield, FiUser, FiPlus, FiTrash2, FiUsers, FiEdit3, 
  FiX, FiCheck, FiExternalLink, FiAlertCircle, FiList, 
  FiSearch, FiAlertTriangle, FiInfo, FiSave, FiUserPlus 
} = FiIcons;

const AdminDashboard = () => {
  const { user, impersonate } = useAuth();
  const [activeTab, setActiveTab] = useState('directory');
  const [data, setData] = useState({ masters: [], pilots: [], deleteRequests: [], allLogs: [] });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [confirmModal, setConfirmModal] = useState({ open: false, logId: null, type: '', title: '', message: '' });
  const [userModal, setUserModal] = useState({ open: false, mode: 'create', userData: null });
  
  // Form State
  const [userForm, setUserForm] = useState({ username: '', nickname: '', role: 'pilot' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => { 
    loadData(); 
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'directory') {
        const { data: users } = await supabase.from(TABLES.USERS).select('*').order('username', { ascending: true });
        setData(prev => ({ 
          ...prev, 
          masters: users?.filter(u => u.role === 'master') || [], 
          pilots: users?.filter(u => u.role === 'pilot') || [] 
        }));
      } else {
        let query = supabase
          .from(TABLES.LOGS)
          .select(`*, pilot:pilot_id(username, nickname), master:master_id(username, nickname), item:item_id(name)`)
          .order('created_at', { ascending: false });

        if (activeTab === 'requests') {
          query = query.eq('status', 'pending_delete');
        } else {
          query = query.limit(100);
        }

        const { data: logs } = await query;
        if (activeTab === 'requests') {
          setData(prev => ({ ...prev, deleteRequests: logs || [] }));
        } else {
          setData(prev => ({ ...prev, allLogs: logs || [] }));
        }
      }
    } catch (err) {
      console.error("Load Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const openUserModal = (u = null) => {
    if (u) {
      setUserModal({ open: true, mode: 'edit', userData: u });
      setUserForm({ username: u.username, nickname: u.nickname || '', role: u.role });
    } else {
      setUserModal({ open: true, mode: 'create', userData: null });
      setUserForm({ username: '', nickname: '', role: 'pilot' });
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      if (userModal.mode === 'create') {
        const { error } = await supabase.from(TABLES.USERS).insert([userForm]);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from(TABLES.USERS)
          .update(userForm)
          .eq('id', userModal.userData.id);
        if (error) throw error;
      }
      setUserModal({ open: false, mode: 'create', userData: null });
      loadData();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const openConfirm = (logId, type) => {
    const isDelete = type === 'delete';
    setConfirmModal({
      open: true,
      logId,
      type,
      title: isDelete ? 'Confirm Permanent Deletion' : 'Reject Deletion Request',
      message: isDelete 
        ? 'This action cannot be undone. This log will be completely erased from the system.' 
        : 'This will return the log to "unpaid" status and notify the master.'
    });
  };

  const handleAction = async () => {
    if (!confirmModal.logId) return;
    setProcessing(true);
    try {
      if (confirmModal.type === 'delete') {
        const { error } = await supabase.from(TABLES.LOGS).delete().eq('id', confirmModal.logId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from(TABLES.LOGS).update({ status: 'unpaid', delete_reason: null }).eq('id', confirmModal.logId);
        if (error) throw error;
      }
      setConfirmModal({ open: false, logId: null, type: '', title: '', message: '' });
      await loadData();
    } catch (err) {
      alert("Operation failed: " + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const filteredUsers = [...data.masters, ...data.pilots].filter(u => 
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.nickname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-12 pb-24">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-5xl font-black text-gray-900 tracking-tight">System Control</h2>
          <div className="flex gap-4 mt-8 bg-gray-200/50 p-1.5 rounded-2xl w-fit backdrop-blur-md border border-white">
            {['directory', 'requests', 'logs'].map(tab => (
              <button 
                key={tab} 
                onClick={() => setActiveTab(tab)} 
                className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-white shadow-md text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                {tab === 'directory' ? 'User Roster' : tab === 'requests' ? 'Delete Requests' : 'Global Ledger'}
              </button>
            ))}
          </div>
        </div>
        {activeTab === 'directory' && (
          <button 
            onClick={() => openUserModal()}
            className="apple-button-primary !py-4 flex items-center gap-2 shadow-2xl shadow-blue-500/20"
          >
            <FiUserPlus className="text-xl" />
            <span className="text-[10px] font-black uppercase tracking-widest">Create New Account</span>
          </button>
        )}
      </header>

      {/* Directory Tab */}
      {activeTab === 'directory' && (
        <div className="space-y-8">
          <div className="relative">
            <FiSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
            <input 
              className="apple-input !pl-16 !py-6 shadow-sm border border-white" 
              placeholder="Search by username or nickname..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map(u => (
              <div key={u.id} className="bento-card flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg ${u.role === 'master' ? 'bg-indigo-600' : 'bg-blue-500'}`}>
                    <FiUser className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900">{u.nickname || u.username}</h4>
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">@{u.username} • {u.role}</p>
                  </div>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                  <button onClick={() => openUserModal(u)} className="p-3 bg-gray-50 text-blue-600 hover:bg-blue-50 rounded-xl transition-all">
                    <FiEdit3 />
                  </button>
                  <button onClick={() => impersonate(u)} className="p-3 bg-gray-50 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all">
                    <FiExternalLink />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requests & Logs tabs remain functionally same as previous fix but with improved layout consistency */}
      {activeTab === 'requests' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {data.deleteRequests.map(req => (
            <div key={req.id} className="bento-card border-l-4 border-l-red-500 hover:shadow-xl transition-all">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h4 className="font-black text-2xl text-gray-900">{req.item?.name}</h4>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1">
                    Master: @{req.master?.username} • Pilot: @{req.pilot?.username}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-black text-red-600">₱{req.value_at_log.toLocaleString()}</p>
                </div>
              </div>
              <div className="bg-red-50 p-5 rounded-2xl mb-8 border border-red-100">
                <p className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                  <FiAlertTriangle /> Deletion Reason
                </p>
                <p className="text-sm text-red-800 font-bold italic leading-relaxed">"{req.delete_reason}"</p>
              </div>
              <div className="flex gap-3 relative z-10">
                <button onClick={() => openConfirm(req.id, 'delete')} className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-red-100 active:scale-95 transition-all">Approve Delete</button>
                <button onClick={() => openConfirm(req.id, 'reject')} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-200">Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="bento-card !p-0 overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#f8f9fa] border-b border-gray-100">
                <tr>
                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Item / Participant</th>
                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Master</th>
                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Value</th>
                  <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.allLogs.map(log => (
                  <tr key={log.id} className="hover:bg-blue-50/30 transition-all">
                    <td className="px-8 py-6">
                      <div className="font-black text-gray-900 text-lg">{log.item?.name}</div>
                      <div className="text-[10px] font-bold text-blue-500 mt-1">Pilot: @{log.pilot?.username}</div>
                    </td>
                    <td className="px-8 py-6 text-xs font-black text-gray-600">@{log.master?.username}</td>
                    <td className="px-8 py-6 text-right font-black text-gray-900 text-lg">₱{log.value_at_log.toLocaleString()}</td>
                    <td className="px-8 py-6 text-center">
                      <button onClick={() => openConfirm(log.id, 'delete')} className="p-4 text-red-500 bg-red-50 rounded-2xl hover:bg-red-600 hover:text-white transition-all"><FiTrash2 /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* USER CREATE/EDIT MODAL */}
      <AnimatePresence>
        {userModal.open && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="w-full max-w-lg bg-white rounded-[3rem] p-10 shadow-2xl">
              <div className="flex justify-between items-center mb-10">
                <h3 className="text-3xl font-black text-gray-900">{userModal.mode === 'create' ? 'Create Account' : 'Edit Account'}</h3>
                <button onClick={() => setUserModal({ open: false, mode: 'create', userData: null })} className="p-3 bg-gray-100 rounded-full hover:bg-gray-200"><FiX /></button>
              </div>
              <form onSubmit={handleUserSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Username (Login ID)</label>
                  <input className="apple-input" placeholder="e.g. pilot_john" value={userForm.username} onChange={e => setUserForm({ ...userForm, username: e.target.value.toLowerCase().replace(/\s/g, '') })} required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Display Nickname</label>
                  <input className="apple-input" placeholder="e.g. John Doe" value={userForm.nickname} onChange={e => setUserForm({ ...userForm, nickname: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">System Role</label>
                  <select className="apple-input" value={userForm.role} onChange={e => setUserForm({ ...userForm, role: e.target.value })} required>
                    <option value="pilot">Pilot (Farmer)</option>
                    <option value="master">Master (Guild Leader)</option>
                  </select>
                </div>
                <button type="submit" disabled={processing} className="w-full py-6 bg-blue-600 text-white rounded-[1.5rem] font-black text-sm uppercase tracking-widest shadow-2xl shadow-blue-500/20 active:scale-95 transition-all flex items-center justify-center gap-3">
                  {processing ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><FiSave /> {userModal.mode === 'create' ? 'Create User' : 'Save Changes'}</>}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* GLOBAL CONFIRMATION MODAL */}
      <AnimatePresence>
        {confirmModal.open && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="w-full max-w-md bg-white rounded-[3rem] p-10 shadow-2xl">
              <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center mb-6 shadow-xl ${confirmModal.type === 'delete' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                <FiAlertTriangle className="text-3xl" />
              </div>
              <h3 className="text-3xl font-black text-gray-900 leading-tight mb-4">{confirmModal.title}</h3>
              <p className="text-gray-500 font-bold text-sm mb-10">{confirmModal.message}</p>
              <div className="flex flex-col gap-3">
                <button onClick={handleAction} disabled={processing} className={`w-full py-5 rounded-[1.5rem] font-black text-xs uppercase tracking-widest text-white transition-all ${confirmModal.type === 'delete' ? 'bg-red-600' : 'bg-blue-600'}`}>
                  {processing ? 'Processing...' : 'Confirm Action'}
                </button>
                <button onClick={() => setConfirmModal({ open: false, logId: null, type: '', title: '', message: '' })} className="w-full py-5 bg-gray-100 text-gray-500 rounded-[1.5rem] font-black text-xs uppercase">Cancel</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;